import React from 'react';
import { MarkdownRender } from '@gov-nx/component/web';
import type {
	DisconnectFromAnotherSubmissionMutation,
	DisconnectFromSubmissionMutationVariables,
} from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { PageCode } from '@gov-nx/module/page';
import type { FormButtonProps } from '@gov-nx/ui/web';
import { Button, ConfirmationPrompt } from '@gov-nx/ui/web';

export const DisconnectFromSubmissionDialog = ({
	isConfirmOpen,
	isDisconnecting,
	onDisconnectSubmit,
	onDisconnectCancel,
}: DisconnectFromAnotherSubmissionMutation) => {
	const { t } = useLocale(PageCode['moje-podani']);

	return (
		<ConfirmationPrompt
			isOpen={isConfirmOpen}
			isDisabled={isDisconnecting}
			isLoading={isDisconnecting}
			onConfirm={onDisconnectSubmit}
			onCancel={onDisconnectCancel}
			strings={{
				title: t('odpojit-od-podani.titulek'),
				buttons: {
					cancel: {
						label: t('akce.odpojit-od-podani-zrusit'),
						wcagLabel: t('akce.odpojit-od-podani-zrusit'),
					},
					confirm: {
						label: t('akce.odpojit-od-podani-potvrdit'),
						wcagLabel: t('akce.odpojit-od-podani-potvrdit'),
					},
				},
			}}>
			<MarkdownRender>{t('odpojit-od-podani.text')}</MarkdownRender>
		</ConfirmationPrompt>
	);
};

interface DisconnectProps extends DisconnectFromAnotherSubmissionMutation {
	variables: DisconnectFromSubmissionMutationVariables;
	buttonProps?: {
		type?: FormButtonProps['type'];
		size?: FormButtonProps['size'];
	};
}

export const DisconnectFromSubmission = ({
	buttonProps,
	variables,
	...props
}: DisconnectProps) => {
	const getButton = useDisconnectButtonDefinition();

	return (
		<div>
			<DisconnectFromSubmissionDialog {...props} />
			<Button
				{...getButton(props.disconnect, variables)}
				{...buttonProps}
			/>
		</div>
	);
};

export const useDisconnectButtonDefinition = () => {
	const { t } = useLocale(PageCode['moje-podani']);

	return (
		open: DisconnectFromAnotherSubmissionMutation['disconnect'],
		variables: DisconnectFromSubmissionMutationVariables
	): FormButtonProps => ({
		color: 'primary',
		type: 'link',
		size: 'm',
		className: 'button-text-bold',
		'aria-label': t('akce.odpojit-od-podani'),
		children: t('akce.odpojit-od-podani'),
		onClick: () => open(variables),
	});
};
